import { useCallback, useEffect, useState } from "react";

import type { SourceControlRepository } from "../types";

import {
  fetchSourceControlProviderDetail,
  fetchSourceControlRepositorySummary,
} from "../api/sourceControl";
import { listSourceControlProvidersAsUser } from "../dashboard-client";

interface State {
  loading: boolean;
  error: string | null;
  repositories: SourceControlRepository[];
}

const INITIAL_STATE: State = { error: null, loading: false, repositories: [] };
type RepositoryLoadMode = "freshness" | "summary";

const repositoryCache = new Map<
  RepositoryLoadMode,
  SourceControlRepository[]
>();
const repositoryInflight = new Map<
  RepositoryLoadMode,
  Promise<SourceControlRepository[]>
>();

interface UseAllRepositoriesOptions {
  freshness?: boolean;
}

export function useAllRepositories(options: UseAllRepositoriesOptions = {}) {
  const mode: RepositoryLoadMode =
    options.freshness === true ? "freshness" : "summary";
  const [state, setState] = useState<State>(() => ({
    ...INITIAL_STATE,
    repositories: repositoryCache.get(mode) ?? [],
  }));

  const loadRepositories = useCallback(
    async (force = false) => {
      if (force) {
        repositoryCache.delete(mode);
        repositoryInflight.delete(mode);
      }
      const cached = repositoryCache.get(mode);
      if (cached) {
        setState({ error: null, loading: false, repositories: cached });
        return;
      }
      setState((current) => ({ ...current, error: null, loading: true }));
      try {
        const repositories = await loadAllRepositories(mode);

        setState({ error: null, loading: false, repositories });
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        setState((current) => ({ ...current, error: message, loading: false }));
      }
    },
    [mode],
  );

  useEffect(() => {
    void loadRepositories();
  }, [loadRepositories]);

  return { ...state, refreshRepositories: () => loadRepositories(true) };
}

async function loadAllRepositories(mode: RepositoryLoadMode) {
  const cached = repositoryCache.get(mode);
  if (cached) {
    return cached;
  }
  const inflight = repositoryInflight.get(mode);
  if (inflight) {
    return await inflight;
  }
  const promise =
    mode === "summary"
      ? fetchSourceControlRepositorySummary().then(sortRepositories)
      : fetchFreshRepositoryDetails();
  repositoryInflight.set(mode, promise);
  try {
    const repositories = await promise;
    repositoryCache.set(mode, repositories);
    return repositories;
  } finally {
    repositoryInflight.delete(mode);
  }
}

async function fetchFreshRepositoryDetails() {
  const allProvidersResult = await listSourceControlProvidersAsUser();
  const { providers } = allProvidersResult.data;

  const detailPromises = providers.map((provider) =>
    fetchSourceControlProviderDetail(provider.provider_id).catch(() => null),
  );

  const details = await Promise.all(detailPromises);
  const allRepositories = details
    .filter((detail): detail is NonNullable<typeof detail> => detail !== null)
    .flatMap((detail) => detail.repositories);

  return sortRepositories(dedupeRepositories(allRepositories));
}

function dedupeRepositories(repositories: SourceControlRepository[]) {
  return Array.from(
    new Map(
      repositories.map((repository) => [repository.repository_id, repository]),
    ).values(),
  );
}

function sortRepositories(repositories: SourceControlRepository[]) {
  return dedupeRepositories(repositories).toSorted((a, b) =>
    a.remote_slug.localeCompare(b.remote_slug),
  );
}
